
import { defineComponent, reactive } from 'vue';
import route from '@/router';
import store from '@/store';

// @ts-ignore
import VueApexCharts from 'vue3-apexcharts';

import { formatDate } from '@/utils/dataFormattings';
import { checkNewToken, checkTokenExists } from '@/utils/token';
import { deleteById, get, post, put } from '@/utils/api';
import cookie from '@/utils/cookie';
import ShortenedUrl from '@/models/ShortenedUrl';
import InputField from '../components/InputField.vue';
import HeaderLinks from '../components/HeaderLinks.vue';
import Button from '../components/Button.vue';

type UrlData = {
	shortenedURL: ShortenedUrl;
	analytics?: Array<string>;
	analyticsButtonText: string;
	chartOptions?: any;
	chartSeries?: any;
	visits: Number;
	editingName: string;
	deleting: Boolean;
};

type NewUrlData = {
	name: string;
	longURL: string;
	userID: string;
};

type State = {
	urls: Array<UrlData>;
	urlsAmount: Number;
	inputData: NewUrlData;

	device: {
		width: any;
		height: any;
	};

	backendUrl: string;

	loading: Boolean;
	error: string;
};

export default defineComponent({
	components: {
		InputField,
		VueApexCharts,
		HeaderLinks,
		Button,
	},

	setup() {
		const state: State = reactive({
			urls: [],
			urlsAmount: -1,
			inputData: {
				name: '',
				longURL: '',
				userID: store.getters.getUserId(), // not working after log in ???
			},

			device: {
				width: window.innerWidth > 0 ? window.innerWidth : screen.width,
				height: window.innerHeight > 0 ? window.innerHeight : screen.height,
			},

			backendUrl: process.env.VUE_APP_BACKEND_URL.replace('api/', ''),

			loading: false,
			error: '',
		});

		const getData = async () => {
			const token = cookie.get('token');

			if (token) {
				const timeout = setTimeout(() => {
					state.loading = true;
				}, 1000);
				const data = await get(`short-urls/${state.inputData.userID}`, token);
				clearTimeout(timeout);
				state.loading = false;

				if (data.error) {
					state.error = data.message;
					return;
				}

				checkTokenExists(data.statusCode, route);

				const newToken = checkNewToken(data);
				if (newToken) {
					await getData();

					return;
				}

				if (data.urls) {
					state.urls = data.urls;
					state.urls.forEach((url) => {
						url.visits = url.analytics ? url.analytics.length : 0;

						url.analyticsButtonText = 'Show chart';

						if (url.analytics) {
							url.analytics.sort();

							const dates: Array<string> = [];
							url.analytics.forEach((visit) => {
								const date = visit.split(' ')[0];
								dates.push(date);
							});

							const uniqueDays = new Set(dates);

							const visitsPerDay: Array<Number> = [];
							uniqueDays.forEach((d) => {
								const visits = dates.filter((day) => day == d).length;
								visitsPerDay.push(visits);
							});

							url.chartOptions = {
								chart: {
									id: url.shortenedURL.id,
								},
								xaxis: {
									categories: Array.from(uniqueDays),
								},
							};
							url.chartSeries = [
								{
									name: 'Analytics',
									data: visitsPerDay,
								},
							];
						}

						url.shortenedURL.createdAt = new Date(url.shortenedURL.createdAt);
					});
				} else {
					state.urlsAmount = 0;
				}
			} else {
				route.replace('/login');
			}
		};

		const submitLongUrl = async (event: Event) => {
			event.preventDefault();

			if (state.inputData.name != '' && state.inputData.longURL != '' && state.inputData.userID != '') {
				const token = cookie.get('token');
				const data = await post('short-urls', state.inputData, token);

				checkTokenExists(data.statusCode, route);

				const newToken = checkNewToken(data);
				if (newToken) {
					await submitLongUrl(event);

					return;
				}

				state.inputData.longURL = state.backendUrl + data.shortenedURL.shortURL;

				const shortenedUrl: ShortenedUrl = data.shortenedURL;

				shortenedUrl.createdAt = new Date(shortenedUrl.createdAt);

				const urlData: UrlData = {
					shortenedURL: shortenedUrl,
					analyticsButtonText: 'Show chart',
					visits: 0,
					editingName: '',
					deleting: false,
				};
				state.urls.push(urlData);

				// Not the best way to do this but atleast it works...
				setTimeout(() => {
					// @ts-ignore
					document.querySelector('#url').select();
					document.execCommand('copy');
				}, 20);
			}
		};

		const updateShortUrl = async (url: UrlData) => {
			if (url.editingName != url.shortenedURL.name && url.editingName != '') {
				const newData = {
					name: url.editingName,
				};

				const token = cookie.get('token');

				const data = await put('short-urls', url.shortenedURL.id, newData, token);

				checkTokenExists(data.statusCode, route);

				const newToken = checkNewToken(data);
				if (newToken) {
					await updateShortUrl(url);

					return;
				}

				url.shortenedURL.name = url.editingName;
			}

			cancelEditingItem(url);
		};

		const toggleDeletingItem = (url: UrlData) => {
			url.deleting = !url.deleting;
		};

		const deleteShortUrl = async (urlIndex: number) => {
			const url = state.urls[urlIndex];

			const token = cookie.get('token');

			const data = await deleteById('short-urls', url.shortenedURL.id, token);

			checkTokenExists(data.statusCode, route);

			const newToken = checkNewToken(data);
			if (newToken) {
				await deleteShortUrl(urlIndex);

				return;
			}

			state.urls.splice(urlIndex, 1);

			if (state.urls.length == 0) {
				state.urlsAmount = 0;
			}
		};

		const editItem = (url: UrlData) => {
			url.editingName = url.shortenedURL.name;
		};

		const cancelEditingItem = (url: UrlData) => {
			url.editingName = '';
		};

		const toggleChart = (url: UrlData) => {
			if (url.analyticsButtonText == 'Show chart') {
				url.analyticsButtonText = 'Hide chart';
			} else {
				url.analyticsButtonText = 'Show chart';
			}
		};

		if (state.inputData.userID) {
			getData();
		} else {
			cookie.delete('token');

			route.push('/home');
			window.location.reload();
		}

		return {
			formatDate,

			state,

			submitLongUrl,
			updateShortUrl,
			cancelEditingItem,
			editItem,
			toggleDeletingItem,
			deleteShortUrl,
			toggleChart,
		};
	},
});
